/**
 * Map a registered asset's serve path to something a media element can load.
 *
 * The backend hands back `/api/assets/{id}/media`, which needs a Bearer token
 * — a header `<img src>` and `<video src>` cannot send. Rewrite those to the
 * same-origin BFF proxy, which attaches the server-side session token.
 * Provider URLs (and anything else) pass through untouched, which is what
 * makes this safe to call on every history item regardless of vintage: rows
 * created before the mirror shipped still hold a fal URL.
 *
 * TWO SHAPES, ONE PROXY. `/api/tools/generations/{id}/media` was the address
 * tool output shipped under before media was generalized across surfaces. Ids
 * were preserved, so the old path still resolves to the same asset — canvases
 * and browser history holding it keep working, and neither has to be
 * rewritten in place. Both are recognized here; only the new one is emitted.
 */

const ASSET_SERVE_PATH_PATTERN = /^\/api\/assets\/[0-9a-fA-F-]{36}\/media$/
const LEGACY_SERVE_PATH_PATTERN =
  /^\/api\/tools\/generations\/[0-9a-fA-F-]{36}\/media$/

export function isAssetServePath(url: string): boolean {
  return (
    ASSET_SERVE_PATH_PATTERN.test(url) || LEGACY_SERVE_PATH_PATTERN.test(url)
  )
}

export function assetMediaSrc(url: string | undefined): string | undefined {
  if (!url || !isAssetServePath(url)) return url
  return `/api/tools/generations/serve?path=${encodeURIComponent(url)}`
}

/**
 * The same asset, addressed for saving rather than playing.
 *
 * Downloads used to land as `serve.png`, `serve (1).png`, `serve (2).png`: the
 * anchor's `download` attribute was empty and the proxy sent no
 * `Content-Disposition`, so the browser fell back to the last path segment of
 * the URL — the route's own name. On a tool whose whole point is running one
 * master through several placements, that leaves a folder of files nobody can
 * tell apart.
 *
 * `stem` is the meaningful part of the name; the proxy appends the extension,
 * because only it has seen the stored asset's real content type. Provider URLs
 * (pre-mirror history) have no proxy to ask and pass through unchanged.
 */
export function assetDownloadSrc(
  url: string | undefined,
  stem: string,
): string | undefined {
  const proxied = assetMediaSrc(url)
  if (!proxied || proxied === url) return proxied
  return `${proxied}&filename=${encodeURIComponent(stem)}`
}
